import { isLocalStorageSupported } from "@multimediallc/web-utils/modernizr"
import { getChatMode } from "./chatSettings"
import { EventRouter, eventsPmSessionsCount } from "./events"
import { fullscreenChange, fullscreenElement, isFullscreen } from "./fullscreen"
import { MobileMetricsVideoMode } from "./mobilelib/sessionMetrics"
import { setCurrentMode, setStreamingMode } from "./newrelic"

export const enum VideoMode {
    Split = "split",
    Theater = "theater",
    IFS = "fullscreen",
    Fullscreen = "noninteractive-fullscreen",
    Fullvideo = "fullvideo", // fullvideo only
    VideoOnly = "videoonly", // embed only
}

export enum StreamingMode {
    HLS = "hls",
    LLHLS = "ll-hls",
    FLLHLS = "fallback hls",
}

export interface IVideoModeChangeNotification {
    previousMode: VideoMode
    currentMode: VideoMode
}

class VideoModeHandler {
    private videoMode: VideoMode
    private storageKey = "isTheaterMode"
    private streamingMode = StreamingMode.HLS
    private lastVideoMode: VideoMode
    private lastNonFullscreenVideoMode: VideoMode
    private videoHeight: number | undefined

    // Base limit + 1 per PM session
    public changeVideoMode = new EventRouter<IVideoModeChangeNotification>("changeVideoMode", { listenersWarningThreshold: () => 20 + eventsPmSessionsCount })
    private isMobile: boolean

    constructor() {
        const initialVideoMode = this.getInitialVideoMode()
        const lastVideoMode = initialVideoMode === VideoMode.Split ? VideoMode.Theater : VideoMode.Split
        this.videoMode = initialVideoMode
        this.lastVideoMode = lastVideoMode
        this.lastNonFullscreenVideoMode = lastVideoMode
        this.saveVideoModeStorage()
        this.listenForFullscreen()
        setCurrentMode(this.videoMode)

        getChatMode.listen((mode: string) => {
            this.isMobile = mode === "mobile"
            if (this.isMobile) {
                if (isFullscreen()){
                    setCurrentMode(MobileMetricsVideoMode.LF)
                } else {
                    setCurrentMode(MobileMetricsVideoMode.PC)
                }
            }
        })
    }

    private listenForFullscreen(): void {
        fullscreenChange.listen(() => {
            if (isFullscreen()) {
                const isChatPlayer = fullscreenElement()?.id === "chat-player"
                // we have broadcaster's video's which can be viewed in fullscreen also, it needs to be ignored
                if (!isChatPlayer) {
                    return
                }
                // fullscreenchange also fires for IFS. wait and check if VideoControls listener sets videoMode to IFS first
                window.setTimeout(() => {
                    if (this.videoMode !== VideoMode.IFS) {
                        videoModeHandler.setFireVideoMode(VideoMode.Fullscreen)
                    }
                }, 0)
            } else {
                videoModeHandler.setFireVideoMode(this.lastNonFullscreenVideoMode)
            }
        })
    }

    private setVideoModeInternal(newVideoMode: VideoMode, force = false): boolean {
        if (!force && newVideoMode === this.videoMode) {
            // TODO Add a metric for where this check fails and proceed to correct the replicated call if possible.
            return false
        }

        const fullscreenModes = [VideoMode.Fullscreen, VideoMode.IFS, VideoMode.Fullvideo]
        if (fullscreenModes.indexOf(this.videoMode) === -1) {
            this.lastNonFullscreenVideoMode = this.videoMode
        }

        this.lastVideoMode = this.videoMode
        this.videoMode = newVideoMode
        if (this.videoMode === VideoMode.Fullscreen && this.isMobile){
            setCurrentMode(MobileMetricsVideoMode.LF)
        } else {
            setCurrentMode(this.videoMode)
        }
        this.saveVideoModeStorage()
        return true
    }

    public setVideoMode(newVideoMode: VideoMode, force = false): void {
        this.setVideoModeInternal(newVideoMode, force)
    }

    public setFireVideoMode(newVideoMode: VideoMode, force?: { update?: boolean, fire?: boolean }): void {
        const wasUpdated = this.setVideoModeInternal(newVideoMode, force?.update ?? false)
        if (wasUpdated || (force?.fire ?? false)) {
            this.changeVideoMode.fire({
                currentMode: this.videoMode,
                previousMode: this.lastVideoMode,
            })
        }
    }

    private saveVideoModeStorage(): void {
        if ([VideoMode.Split, VideoMode.Theater].includes(this.videoMode) && isLocalStorageSupported()) {
            window.localStorage.setItem(this.storageKey, JSON.stringify({ "isTheaterMode": this.videoMode === VideoMode.Theater }))
        }
    }

    private getInitialVideoMode(): VideoMode {
        let initialMode = VideoMode.Split

        if (isLocalStorageSupported()) {
            const savedSettings = window.localStorage.getItem(this.storageKey)
            if (savedSettings !== null) {
                const isTheaterMode_Parsed = JSON.parse(savedSettings)["isTheaterMode"]
                initialMode = Boolean(isTheaterMode_Parsed) ? VideoMode.Theater : VideoMode.Split
            }
        }

        return initialMode
    }

    public getVideoMode(): VideoMode {
        return this.videoMode
    }

    public getStreamingMode(): StreamingMode {
        return this.streamingMode
    }

    public setStreamingMode(newStreamingMode: StreamingMode): void {
        this.streamingMode = newStreamingMode
        setStreamingMode(this.streamingMode)
    }

    public setVideoHeight(height: number): void {
        this.videoHeight = height
    }

    public getVideoHeight(): number | undefined {
        return this.videoHeight
    }

    public getPreviousNonFullscreenVideoMode(): VideoMode {
        return this.lastNonFullscreenVideoMode
    }

}

export const videoModeHandler = new VideoModeHandler()
